import { ChartDataPoint, Dimension, Goal } from 'Generated/graphql-types'
import React, { useEffect, useState } from 'react'
import { Menu, MenuItem, Button as BirdButton } from '@resiliatech/birdsong'
import moment from 'moment/moment'
import { Moment } from 'moment'
import _ from 'lodash'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import LegendToggleOutlinedIcon from '@mui/icons-material/LegendToggleOutlined'
import {
  Box,
  Switch,
  FormControlLabel,
  styled,
  Button,
  Typography,
  TextField,
  Checkbox,
  Divider,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/TableRows'
import { ChartTypeOptionsEnum } from '../../GoalPage/types'
import {
  ButtonContainer,
  ContentWrapper,
} from '../../GoalPage/Tasks/RecurringTasks.styled'
import { HeaderAndButtonContainer } from '../../GoalPage/Updates/UpdatesTab.styled'
import { ChartDataTable } from '../../GoalPage/ChartDataTable/ChartDataTable'
import { useAppState } from '../../../../../Context/AppContext/AppContext'
import { analytics } from '../../../../../Modules/Analytics'
import { initiatives } from '../../../../../Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { GoalSectionEmptyState } from '../../GoalPage/EmptyStates/GoalSectionEmptyState'
import CreateChartWizard from '../../../../Charts/MultiLineChart/CreateChartWizard'
import GET_DIMENSIONS from '../../../../../Apollo/Queries/GetDimensions'
import { ChartCard } from './ChartCard'
import { ALL_CHART_CATEGORIES } from '../../../../../Apollo/Queries/AllChartCategories'
import {
  CREATE_CHART_CATEGORIES,
  UPDATE_CHART_CATEGORY,
} from '../../../../../Apollo/Mutations/ChartCategory'
import { CHART_CATEGORY } from '../../../../../Apollo/Queries/ChartCategory'
import { CREATE_CHART_DATA_POINTS } from '../../../../../Apollo/Mutations/ChartDataPoint'
import { GET_CATEGORY_BY_TITLE } from '../../../../../Apollo/Queries/GetCategoryByTitle'

type GoalProgessUpdatesTabProps = {
  goal: Goal
  isGoalSharedWithFunder: boolean
}

export type RowType = {
  date: string
  values: (Omit<ChartDataPoint, 'id'> & { id: number | null })[]
}

export const GoalProgessUpdatesTab = ({
  goal,
  isGoalSharedWithFunder,
}: GoalProgessUpdatesTabProps) => {
  const [isAddingData, setIsAddingData] = useState(false)
  // These state variables are set in onEdit but not currently used elsewhere - keeping for future functionality
  const [, setIsEditingData] = useState(false)
  const [, setEditingRow] = useState<RowType | undefined>()
  const [isCreatingChart, setIsCreatingChart] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  // Define types for categories and data points
  interface ChartDataPointType {
    id?: number
    chartId?: number
    chartCategoryId?: number
    date: string | Date
    value?: number | null
    archivedAt: string | Date | null
  }

  interface ChartCategoryType {
    id: number
    title: string
    chartId: number
    archivedAt: string | Date | null
    ChartDataPoint?: ChartDataPointType[]
  }

  const [allCategories, setAllCategories] = useState<ChartCategoryType[]>([])
  const [newCategoryName, setNewCategoryName] = useState('')
  const [categoryError, setCategoryError] = useState<string | null>(null)
  const [chartType, setChartType] = useState<ChartTypeOptionsEnum>(
    ChartTypeOptionsEnum.dot
  )

  const [newEntryDate, setNewEntryDate] = useState<Moment | null>(
    moment().utc()
  )

  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId, role, nonprofitOrgRole },
  } = useAppState()

  const { data: { getDimensions: dimensions = [] } = {} } = useQuery(
    GET_DIMENSIONS,
    {
      variables: { nonprofitOrgId },
    }
  )

  const { data: chartCategoryData, refetch: refetchChartCategories } = useQuery(
    ALL_CHART_CATEGORIES,
    {
      variables: { chartId: goal.chartId },
      skip: !goal.chartId,
      fetchPolicy: 'cache-and-network', // Ensure we get fresh data after updates
    }
  )

  // Also query the standard chart category endpoint to keep it in sync
  const { refetch: refetchStandardChartCategories } = useQuery(CHART_CATEGORY, {
    variables: { chartId: goal.chartId },
    skip: !goal.chartId,
    fetchPolicy: 'cache-and-network',
  })

  // Refetch all relevant queries to ensure UI is updated
  const refetchAllQueries = () => {
    refetchChartCategories()
    refetchStandardChartCategories()
  }

  const [updateChartCategory] = useMutation(UPDATE_CHART_CATEGORY, {
    onCompleted: () => {
      refetchAllQueries()
    },
  })

  const [createChartCategories] = useMutation(CREATE_CHART_CATEGORIES)

  // Add a lazy query specifically for getting a category by title
  const [getCategoryByTitle] = useLazyQuery(GET_CATEGORY_BY_TITLE, {
    fetchPolicy: 'network-only', // Always fetch from server, not cache
  })

  const [createChartDataPoints] = useMutation(CREATE_CHART_DATA_POINTS, {
    onCompleted: () => {
      refetchAllQueries()
    },
  })

  useEffect(() => {
    if (chartCategoryData?.allChartCategories) {
      setAllCategories(chartCategoryData.allChartCategories)

      // Select all non-archived categories by default
      const nonArchivedCategories = chartCategoryData.allChartCategories
        .filter((category: ChartCategoryType) => !category.archivedAt)
        .map((category: ChartCategoryType) => category.id)

      setSelectedCategories(nonArchivedCategories)
    }
  }, [chartCategoryData])

  const handleAddDataClick = () => {
    // Switch to table mode if we're currently in chart mode
    if (viewMode === 'chart') {
      setViewMode('table')
    }
    setIsAddingData(true)
    setEditingRow(undefined)
    analytics.trackActionCreator(initiatives.Create_Data_Point_Clicked)({
      role,
      relationship: nonprofitOrgRole,
      goalId: goal.id,
    })
  }

  const handleAddChart = () => {
    setIsCreatingChart(true)
  }

  const onEdit = (row: {
    date: string
    values: (Omit<ChartDataPoint, 'id'> & { id: number | null })[]
  }) => {
    setIsAddingData(false)
    setIsEditingData(true)
    setEditingRow(row)
  }

  const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViewMode(event.target.checked ? 'chart' : 'table')
  }

  const handleChartTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChartType(
      event.target.checked
        ? ChartTypeOptionsEnum.line
        : ChartTypeOptionsEnum.dot
    )
  }

  const handleCategoriesClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCategoriesClose = () => {
    setAnchorEl(null)
    // Reset the new category input and any errors when closing the menu
    setNewCategoryName('')
    setCategoryError(null)
  }

  const handleCategoryToggle = (categoryId: number) => {
    setSelectedCategories((prev) => {
      // If trying to uncheck and this is the only selected category, prevent it
      if (prev.includes(categoryId) && prev.length === 1) {
        return prev // Return the current state unchanged
      }

      // Otherwise perform normal toggle behavior
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId)
      }
      return [...prev, categoryId]
    })
  }

  const handleNewCategoryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.trim()
    setNewCategoryName(event.target.value)

    // Check for duplicate category name
    if (value) {
      const isDuplicate = allCategories.some(
        (category) => category.title.toLowerCase() === value.toLowerCase()
      )

      if (isDuplicate) {
        setCategoryError('This category already exists')
      } else {
        setCategoryError(null)
      }
    } else {
      setCategoryError(null)
    }
  }

  // Helper function to get all unique dates from active (non-archived) data points
  const getExistingDataPoints = () => {
    if (!allCategories || allCategories.length === 0) return []

    // Track which dates are active vs archived
    // We'll use this to determine which dates to create data points for
    interface DateInfo {
      dateStr: string // Original ISO string
      activeCount: number // Number of categories with active data points for this date
      archivedCount: number // Number of categories with archived data points for this date
    }

    // Map of date key (YYYY-MM-DD) to date info
    const dateMap = new Map<string, DateInfo>()

    allCategories.forEach((category: ChartCategoryType) => {
      if (category.ChartDataPoint && category.ChartDataPoint.length > 0) {
        category.ChartDataPoint.forEach((dataPoint: ChartDataPointType) => {
          if (dataPoint.date) {
            try {
              // Use moment with UTC parsing for consistent timezone handling
              const momentDate = moment.utc(dataPoint.date)
              const dateKey = momentDate.format('YYYY-MM-DD')

              // Get the original date string
              let originalDateStr: string
              if (dataPoint.date instanceof Date) {
                originalDateStr = dataPoint.date.toISOString()
              } else if (typeof dataPoint.date === 'string') {
                originalDateStr = dataPoint.date
              } else {
                originalDateStr = momentDate.toISOString()
              }

              // Initialize or update date info
              if (!dateMap.has(dateKey)) {
                dateMap.set(dateKey, {
                  dateStr: originalDateStr,
                  activeCount: 0,
                  archivedCount: 0,
                })
              }

              // Update active/archived counts
              const dateInfo = dateMap.get(dateKey)!
              if (dataPoint.archivedAt) {
                dateInfo.archivedCount++
              } else {
                dateInfo.activeCount++
              }
            } catch (e) {
              // Silently handle date parsing errors
            }
          }
        })
      }
    })

    // Only include dates that have at least one active (non-archived) data point
    // This ensures we don't create data points for dates that are completely archived
    const activeDates = Array.from(dateMap.values())
      .filter((info) => info.activeCount > 0)
      .map((info) => info.dateStr)

    // Sort dates chronologically
    return activeDates.sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    )
  }

  // Create data points for a new category matching ALL existing dates from any category
  const createDataPointsForNewCategory = async (newCategoryId: number) => {
    try {
      // Get all unique dates that have data points across any category
      // The dates are returned as ISO strings with the exact same timezone info as the original data points
      const existingDates = getExistingDataPoints()

      // If there are no existing data points, create one with today's date
      if (existingDates.length === 0) {
        // Use ISO string format with UTC time to avoid timezone issues
        const todayStr = moment().utc().toISOString()
        existingDates.push(todayStr)
      }

      // Create data points for the new category for EVERY existing date
      // This ensures the new category has matching data points with all other categories
      const dataPoints = existingDates.map((dateStr) => ({
        chartId: goal.chartId,
        chartCategoryId: newCategoryId,
        // Use the exact ISO string format from the original data points
        date: dateStr,
        value: 0, // Default value for new category data points (starts at zero)
      }))

      // Create all the data points in a single API call
      if (dataPoints.length > 0) {
        try {
          // Create all data points in one operation
          await createChartDataPoints({
            variables: {
              data: dataPoints,
            },
          })
        } catch (err) {
          // Fallback to one-by-one creation if bulk operation fails
          const createPromises = dataPoints.map(
            (dataPoint) =>
              createChartDataPoints({
                variables: {
                  data: [dataPoint], // Create one at a time
                },
              }).catch(() => null) // Ignore individual errors
          )

          // Wait for all creation attempts to finish
          await Promise.all(createPromises)
        }
      }
    } catch (error) {
      // Silently handle errors - we'll refresh the UI anyway
      // The user can try again if it didn't work completely
    }
  }

  const handleApplyCategories = async () => {
    // First, verify we have at least one category selected
    if (selectedCategories.length === 0) {
      // This is a safety check, though the UI should prevent this state
      return
    }

    // Next, create any new category if entered and not a duplicate
    if (newCategoryName.trim() && !categoryError) {
      try {
        // Create the new category
        await createChartCategories({
          variables: {
            chartId: goal.chartId,
            data: [{ title: newCategoryName.trim() }],
          },
        })

        // Give a little time for the database to process the new category
        await new Promise((resolve) => {
          setTimeout(resolve, 500)
        })

        // Query explicitly for the category we just created, using the exact title
        const { data: categoryData } = await getCategoryByTitle({
          variables: {
            chartId: goal.chartId,
            title: newCategoryName.trim(),
          },
        })

        // If we found the category, use its ID to create data points
        if (categoryData?.categoryByTitle) {
          const newCategoryId = categoryData.categoryByTitle.id

          // Update selected categories
          setSelectedCategories((prev) => [...prev, newCategoryId])

          // Create data points for the new category
          await createDataPointsForNewCategory(newCategoryId)

          // Force refresh all data after everything is done
          await refetchAllQueries()
        } else {
          // Force refresh all data even when there was an issue
          await refetchAllQueries()
        }
      } catch (error) {
        // Force refresh all data even when there was an error
        await refetchAllQueries()
      }
    }

    // Update category archive status based on selection
    const updatePromises = allCategories.map((category) => {
      const isSelected = selectedCategories.includes(category.id)
      const isArchived = !!category.archivedAt

      // We need to update when:
      // 1. Category is selected but currently archived (need to unarchive)
      // 2. Category is not selected but currently not archived (need to archive)
      if ((isSelected && isArchived) || (!isSelected && !isArchived)) {
        return updateChartCategory({
          variables: {
            data: {
              chartCategoryId: category.id,
              title: category.title,
              shouldArchive: !isSelected, // Archive if not selected, unarchive if selected
            },
          },
        })
      }
      return Promise.resolve()
    })

    await Promise.all(updatePromises)

    // Force refetch all queries to ensure UI is updated with latest data
    refetchAllQueries()

    // Close the categories menu
    handleCategoriesClose()
  }

  return (
    <ContentWrapper>
      <HeaderAndButtonContainer style={{ paddingBottom: '8px' }}>
        <ViewToggleContainer>
          <StyledBox className={!goal.hasUpdates ? 'disabled' : ''}>
            <Typography
              variant='body2'
              component='span'
              onClick={() => (!goal.hasUpdates ? null : setViewMode('table'))}
              sx={{ cursor: goal.hasUpdates ? 'pointer' : 'default' }}
            >
              Table
            </Typography>
            <FormControlLabel
              style={{ marginLeft: '5px', width: '40px' }}
              control={
                <Switch
                  disabled={!goal.hasUpdates}
                  checked={viewMode === 'chart'}
                  onChange={handleViewChange}
                />
              }
              label=''
            />
            <Typography
              variant='body2'
              component='span'
              onClick={() => (!goal.hasUpdates ? null : setViewMode('chart'))}
              sx={{ cursor: goal.hasUpdates ? 'pointer' : 'default' }}
            >
              Chart
            </Typography>
          </StyledBox>

          {viewMode === 'table' ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '20px',
                }}
              >
                <CategoryButton
                  startIcon={<FilterListIcon />}
                  onClick={handleCategoriesClick}
                >
                  Categories
                </CategoryButton>
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCategoriesClose}
                PaperProps={{
                  style: {
                    width: '300px',
                    padding: '16px 0',
                  },
                }}
              >
                <Box sx={{ px: 2, pb: 2 }}>
                  <TextField
                    fullWidth
                    label='Add new category'
                    variant='outlined'
                    size='small'
                    value={newCategoryName}
                    onChange={handleNewCategoryChange}
                    placeholder='Enter category name'
                    error={!!categoryError}
                    helperText={categoryError}
                  />
                </Box>
                <Divider />
                <Box sx={{ maxHeight: '300px', overflow: 'auto', py: 1 }}>
                  {allCategories.map((category) => (
                    <MenuItem
                      key={category.id}
                      onClick={() => handleCategoryToggle(category.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Checkbox
                        checked={selectedCategories.includes(category.id)}
                        sx={{ padding: '4px 8px 4px 0' }}
                      />
                      <Typography>{category.title}</Typography>
                    </MenuItem>
                  ))}
                </Box>
                <Divider />
                <Box
                  sx={{
                    px: 2,
                    pt: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleApplyCategories}
                    disabled={!!categoryError}
                  >
                    Apply
                  </Button>
                </Box>
              </Menu>
            </>
          ) : (
            <StyledBox
              sx={{ display: 'flex', alignItems: 'center', marginLeft: '20px' }}
            >
              <Typography
                variant='body2'
                sx={{ marginRight: '8px', cursor: 'pointer' }}
                onClick={() => setChartType(ChartTypeOptionsEnum.dot)}
              >
                Dots
              </Typography>
              <FormControlLabel
                style={{ margin: '0', width: '40px' }}
                control={
                  <Switch
                    checked={chartType === ChartTypeOptionsEnum.line}
                    onChange={handleChartTypeChange}
                    size='small'
                  />
                }
                label=''
              />
              <Typography
                variant='body2'
                sx={{ marginLeft: '8px', cursor: 'pointer' }}
                onClick={() => setChartType(ChartTypeOptionsEnum.line)}
              >
                Line
              </Typography>
            </StyledBox>
          )}
        </ViewToggleContainer>

        <ButtonContainer>
          {goal.hasChart ? (
            <BirdButton
              variant='table'
              label='Add Progress'
              onClick={handleAddDataClick}
            />
          ) : (
            <BirdButton
              variant='primary'
              label='Add Chart'
              onClick={handleAddChart}
            />
          )}
        </ButtonContainer>
      </HeaderAndButtonContainer>
      <div>
        {!goal.hasChart && (
          <GoalSectionEmptyState
            icon={<LegendToggleOutlinedIcon />}
            message='Add data to create a chart and visualize your progress'
          />
        )}
        {goal.hasChart && viewMode === 'table' && (
          <ChartDataTable
            onEmptyStateAddData={handleAddDataClick}
            simple
            goal={goal}
            isAddingData={isAddingData}
            newEntryDate={newEntryDate}
            onAddData={() => setIsAddingData(false)}
            onEditData={onEdit}
            onChangeNewEntryDate={setNewEntryDate}
            isGoalSharedWithFunder={isGoalSharedWithFunder}
            hideFilters
          />
        )}
        {goal.hasChart && viewMode === 'chart' && (
          <ChartCard goal={goal} chartType={chartType} />
        )}
      </div>
      {!goal.hasChart && (
        <CreateChartWizard
          allDimensions={_.orderBy(dimensions as Dimension[], 'title', 'asc')}
          goalId={goal.id}
          goalTitle={goal.title}
          isOpen={isCreatingChart}
          onToggle={setIsCreatingChart}
          isGoalSharedWithFunder={isGoalSharedWithFunder}
        />
      )}
    </ContentWrapper>
  )
}

const CategoryButton = styled(Button)`
  text-transform: none;
  padding: 8px 16px;
  font-weight: 500;

  .MuiButton-startIcon {
    margin-right: 8px;
  }
`

// Styled components for the view toggles and categories button
const ViewToggleContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledBox = styled(Box)`
  &.disabled {
    opacity: 0.4;
  }
`
